import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { apiErrorMessage } from '../api';
import { help } from '../helpTexts';
import { chipButtonSx, launchColors } from '../launchTheme';
import InfoTip from '../ui/InfoTip';
import SearchField from '../ui/SearchField';
import { StatusPill } from '../ui/StatusPill';
import { alcoholApi } from './alcoholApi';
import { alcoholCommandId } from './alcoholLabels';
import { AlcoholPrompts, AlcoholProducer, PROMPT_LABELS, WINERY_LIMIT } from './alcoholTypes';
import GeneratedTextField from './GeneratedTextField';
import TextHistoryDialog from './TextHistoryDialog';

interface Props {
  open: boolean;
  canEdit: boolean;
  prompts: AlcoholPrompts | null;
  names: Record<string, string>;
  onClose: () => void;
  onChanged: () => void;
  onShowProducts: (producer: string) => void;
}

const ProducerEditor: React.FC<{
  producer: AlcoholProducer;
  canEdit: boolean;
  promptReady: boolean;
  names: Record<string, string>;
  onSaved: (producer: AlcoholProducer) => void;
  onConflict: () => void;
  onError: (message: string) => void;
}> = ({ producer, canEdit, promptReady, names, onSaved, onConflict, onError }) => {
  const [suggestion, setSuggestion] = useState<string | undefined>(undefined);
  const [generating, setGenerating] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const loadHistory = useCallback(() => alcoholApi.producerHistory(producer.id), [producer.id]);

  const save = async (text: string) => {
    try {
      onSaved(await alcoholApi.producerCommand(producer.id, { commandId: alcoholCommandId(), expectedVersion: producer.version, wineryText: text }));
    } catch (requestError: any) {
      if (requestError?.response?.status === 409) onConflict();
      else onError(apiErrorMessage(requestError, 'Vingårdstexten kunde inte sparas.'));
    }
  };

  const generate = async (text: string) => {
    setGenerating(true);
    try {
      const result = await alcoholApi.generateWinery(producer.id, text);
      setSuggestion(result.wineryText ?? '');
    } catch (requestError) {
      onError(apiErrorMessage(requestError, 'Texten kunde inte skapas.'));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Box sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
      <GeneratedTextField
        label={PROMPT_LABELS.winery}
        value={producer.wineryText}
        limit={WINERY_LIMIT}
        canEdit={canEdit}
        promptReady={promptReady}
        generating={generating}
        suggestion={suggestion}
        onGenerate={(draft) => { void generate(draft); }}
        onSave={(text) => { void save(text); }}
        onUseSuggestion={(text) => { void save(text); setSuggestion(undefined); }}
        onDismissSuggestion={() => setSuggestion(undefined)}
        onHistory={() => setHistoryOpen(true)}
      />
      <TextHistoryDialog
        open={historyOpen}
        title={`Historik: Beskrivning vingård, ${producer.name}`}
        label={PROMPT_LABELS.winery}
        current={producer.wineryText}
        names={names}
        canRestore={canEdit}
        load={loadHistory}
        onRestore={(text) => { void save(text); setHistoryOpen(false); }}
        onClose={() => setHistoryOpen(false)}
      />
    </Box>
  );
};

const ProducersPanel: React.FC<Props> = ({ open, canEdit, prompts, names, onClose, onChanged, onShowProducts }) => {
  const [producers, setProducers] = useState<AlcoholProducer[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    alcoholApi.producers()
      .then(setProducers)
      .catch((requestError) => setError(apiErrorMessage(requestError, 'Producenterna kunde inte hämtas.')));
  }, []);

  useEffect(() => {
    if (!open) return;
    setProducers(null);
    setNotice(null);
    setOpenId(null);
    load();
  }, [load, open]);

  const shown = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('sv');
    return (producers ?? []).filter((producer) => (!onlyMissing || !producer.wineryText.trim()) && (!needle || producer.name.toLocaleLowerCase('sv').includes(needle)));
  }, [onlyMissing, producers, query]);
  const missing = (producers ?? []).filter((producer) => !producer.wineryText.trim()).length;
  const promptReady = Boolean(prompts?.winery?.trim());

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>Producenter</span>
          <InfoTip text={help.alcoholProducers} />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <SearchField label="Sök producent" value={query} onChange={setQuery} sx={{ flex: '1 1 240px' }} />
            <Button aria-pressed={onlyMissing} onClick={() => setOnlyMissing((value) => !value)} sx={chipButtonSx(onlyMissing)}>
              Saknar vingårdstext ({missing})
            </Button>
          </Stack>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {notice && <Alert severity="info" onClose={() => setNotice(null)}>{notice}</Alert>}
          {!producers && !error && <LinearProgress />}
          {producers && shown.length === 0 && (
            <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>{producers.length ? 'Inga producenter matchar.' : 'Inga producenter ännu.'}</Typography>
          )}
          <Box role="list" aria-label="Producenter" sx={{ border: shown.length ? `1px solid ${launchColors.divider}` : 'none', borderRadius: '10px', overflow: 'hidden' }}>
            {shown.map((producer) => {
              const expanded = openId === producer.id;
              const hasText = Boolean(producer.wineryText.trim());
              return (
                <Box key={producer.id} role="listitem" sx={{ borderTop: `1px solid ${launchColors.rowDivider}`, '&:first-of-type': { borderTop: 'none' } }}>
                  <Stack direction="row" alignItems="center" sx={{ '&:hover': { bgcolor: launchColors.section } }}>
                    <ButtonBase
                      onClick={() => setOpenId(expanded ? null : producer.id)}
                      aria-expanded={expanded}
                      sx={{ flex: 1, justifyContent: 'flex-start', gap: 1, px: 1.5, py: 1, textAlign: 'left' }}
                    >
                      {expanded ? <ExpandMoreIcon sx={{ fontSize: 18 }} /> : <ChevronRightIcon sx={{ fontSize: 18 }} />}
                      <Typography component="span" sx={{ fontSize: 14, fontWeight: 700, color: launchColors.navy, flex: 1 }}>{producer.name}</Typography>
                      <Typography component="span" sx={{ fontSize: 12, color: launchColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
                        {producer.productCount} {producer.productCount === 1 ? 'produkt' : 'produkter'}
                      </Typography>
                      <StatusPill
                        label={hasText ? 'Har text' : 'Saknar text'}
                        fg={hasText ? launchColors.mossDark : launchColors.warningText}
                        bg={hasText ? launchColors.mossLight : launchColors.warningBg}
                      />
                    </ButtonBase>
                    {producer.productCount > 0 && (
                      <Button size="small" onClick={() => onShowProducts(producer.name)} sx={{ mr: 1, whiteSpace: 'nowrap' }}>Visa produkter</Button>
                    )}
                  </Stack>
                  {expanded && (
                    <ProducerEditor
                      producer={producer}
                      canEdit={canEdit}
                      promptReady={promptReady}
                      names={names}
                      onSaved={(next) => {
                        setProducers((current) => (current ?? []).map((item) => (item.id === next.id ? next : item)));
                        onChanged();
                      }}
                      onConflict={() => {
                        setNotice('Någon annan hann ändra vingårdstexten. Listan har laddats om.');
                        load();
                      }}
                      onError={setError}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Stäng</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProducersPanel;
